/**
 * ForstaArsredovisningenPage - Guide for first annual report
 *
 * SEO Target: /arsredovisning/guider/forsta-arsredovisningen
 * Primary keywords: första årsredovisningen, årsredovisning nystartat företag
 */

import { Link } from "react-router-dom";
import { PageMeta, getBreadcrumbSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";

const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const WarningIcon = () => (
  <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
      clipRule="evenodd"
    />
  </svg>
);

export function ForstaArsredovisningenPage() {
  const breadcrumbItems = [
    { label: "Årsredovisning", href: "/arsredovisning" },
    { label: "Guider", href: "/arsredovisning/guider" },
    { label: "Första årsredovisningen" },
  ];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Årsredovisning", url: "/arsredovisning" },
    { name: "Guider", url: "/arsredovisning/guider" },
    { name: "Första årsredovisningen", url: "/arsredovisning/guider/forsta-arsredovisningen" },
  ]);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Första årsredovisningen - Komplett guide för nystartade företag",
    description:
      "Steg-för-steg guide för att skapa din första årsredovisning som nystartat aktiebolag. Deadlines, innehåll och praktiska tips.",
    author: {
      "@type": "Organization",
      name: "Kvanten",
    },
    publisher: {
      "@type": "Organization",
      name: "Kvanten",
      url: SEO_CONFIG.siteUrl,
    },
    datePublished: "2025-01-15",
    dateModified: "2025-01-30",
  };

  return (
    <>
      <PageMeta
        title="Första Årsredovisningen | Guide för Nystartade Aktiebolag | Kvanten"
        description="Komplett guide för att skapa din första årsredovisning. Lär dig om deadlines, obligatoriskt innehåll och vanliga misstag. Steg-för-steg instruktioner."
        keywords="första årsredovisningen, årsredovisning nystartat företag, första bokslut aktiebolag, årsredovisning ny företagare"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/arsredovisning/guider/forsta-arsredovisningen`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <span>Guide</span>
            <span>•</span>
            <span>10 min läsning</span>
            <span>•</span>
            <span>Uppdaterad jan 2025</span>
          </div>

          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Första årsredovisningen: Komplett guide för nystartade företag
          </h1>

          <p className="text-xl text-gray-600">
            Att skapa din första årsredovisning kan kännas överväldigande. Den här guiden tar dig
            igenom hela processen steg för steg - från förberedelser till inlämning hos
            Bolagsverket.
          </p>
        </div>
      </section>

      {/* Content */}
      <article className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Table of contents */}
          <div className="bg-gray-50 rounded-xl p-6 mb-12">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Innehåll</h2>
            <nav>
              <ol className="space-y-2 text-blue-600">
                <li>
                  <a href="#vad-ar" className="hover:underline">
                    1. Vad är en årsredovisning?
                  </a>
                </li>
                <li>
                  <a href="#deadlines" className="hover:underline">
                    2. Viktiga deadlines
                  </a>
                </li>
                <li>
                  <a href="#forsta-rakenskapsar" className="hover:underline">
                    3. Särskilt om första räkenskapsåret
                  </a>
                </li>
                <li>
                  <a href="#innehall" className="hover:underline">
                    4. Vad ska ingå?
                  </a>
                </li>
                <li>
                  <a href="#steg-for-steg" className="hover:underline">
                    5. Steg-för-steg process
                  </a>
                </li>
                <li>
                  <a href="#vanliga-misstag" className="hover:underline">
                    6. Vanliga misstag att undvika
                  </a>
                </li>
                <li>
                  <a href="#tips" className="hover:underline">
                    7. Praktiska tips
                  </a>
                </li>
              </ol>
            </nav>
          </div>

          {/* Section 1 */}
          <section id="vad-ar" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Vad är en årsredovisning?</h2>
            <p className="text-gray-600 mb-4">
              En årsredovisning är en offentlig handling som sammanfattar företagets ekonomiska
              ställning under räkenskapsåret. Alla aktiebolag är skyldiga enligt lag att upprätta
              och lämna in årsredovisning till Bolagsverket.
            </p>
            <p className="text-gray-600 mb-4">Årsredovisningen ska ge en rättvisande bild av:</p>
            <ul className="space-y-2 mb-4">
              {[
                "Företagets resultat (vinst eller förlust)",
                "Företagets ekonomiska ställning (tillgångar och skulder)",
                "Viktiga händelser under året",
                "Framtidsutsikter",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckIcon />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 2 */}
          <section id="deadlines" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Viktiga deadlines</h2>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-3">
                <WarningIcon />
                <div>
                  <h3 className="font-semibold text-amber-900">Missa inte deadlines!</h3>
                  <p className="text-amber-800 text-sm mt-1">
                    Sen inlämning kan leda till förseningsavgifter på upp till 20 000 kr och i
                    värsta fall likvidation av bolaget.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Årsstämma</h3>
                <p className="text-gray-600">
                  <strong>Senast 6 månader</strong> efter räkenskapsårets slut ska årsstämma hållas
                  där årsredovisningen fastställs.
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Exempel: Räkenskapsår jan-dec → Årsstämma senast 30 juni
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Inlämning till Bolagsverket</h3>
                <p className="text-gray-600">
                  <strong>Senast 7 månader</strong> efter räkenskapsårets slut ska årsredovisningen
                  vara inlämnad och registrerad hos Bolagsverket.
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Exempel: Räkenskapsår jan-dec → Inlämnad senast 31 juli
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Skattedeklaration (INK2)</h3>
                <p className="text-gray-600">
                  Inkomstdeklaration för aktiebolag ska lämnas <strong>senast 1 juli</strong>{" "}
                  (alternativt 1 augusti med anstånd eller vid digitalt).
                </p>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section id="forsta-rakenskapsar" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              3. Särskilt om första räkenskapsåret
            </h2>
            <p className="text-gray-600 mb-4">
              Det första räkenskapsåret för ett nystartat aktiebolag har några speciella egenskaper:
            </p>

            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Förlängt första räkenskapsår</h3>
                <p className="text-gray-600">
                  Det första räkenskapsåret får vara upp till <strong>18 månader</strong> långt.
                  Detta kan vara fördelaktigt för att samla mer omsättning innan första bokslutet.
                </p>
              </div>

              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Inga jämförelsetal</h3>
                <p className="text-gray-600">
                  Eftersom det inte finns något föregående år att jämföra med, behöver du inte ange
                  jämförelsetal i din första årsredovisning. Flerårsöversikten innehåller endast ett
                  år.
                </p>
              </div>

              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Startdatum = Registreringsdatum</h3>
                <p className="text-gray-600">
                  Räkenskapsåret börjar från det datum bolaget registrerades hos Bolagsverket, inte
                  från när ni började bedriva verksamhet.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section id="innehall" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Vad ska ingå?</h2>
            <p className="text-gray-600 mb-6">
              En komplett årsredovisning för ett mindre aktiebolag (K2) ska innehålla:
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Förvaltningsberättelse</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Verksamhetsbeskrivning</li>
                  <li>• Väsentliga händelser</li>
                  <li>• Flerårsöversikt (4 år)</li>
                  <li>• Förändring av eget kapital</li>
                  <li>• Resultatdisposition</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Resultaträkning</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Nettoomsättning</li>
                  <li>• Rörelsens kostnader</li>
                  <li>• Rörelseresultat</li>
                  <li>• Finansiella poster</li>
                  <li>• Årets resultat</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Balansräkning</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Anläggningstillgångar</li>
                  <li>• Omsättningstillgångar</li>
                  <li>• Eget kapital</li>
                  <li>• Skulder</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Noter</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Redovisningsprinciper</li>
                  <li>• Anläggningstillgångar</li>
                  <li>• Personal (om anställda finns)</li>
                  <li>• Ställda säkerheter</li>
                  <li>• Eventualförpliktelser</li>
                </ul>
              </div>
            </div>

            <p className="text-sm text-gray-500 mt-6">
              Läs mer i vår{" "}
              <Link to="/arsredovisning/guider/noter" className="text-blue-600 hover:underline">
                guide om noter
              </Link>{" "}
              och{" "}
              <Link
                to="/arsredovisning/guider/forvaltningsberattelse"
                className="text-blue-600 hover:underline"
              >
                förvaltningsberättelse
              </Link>
              .
            </p>
          </section>

          {/* Section 5 */}
          <section id="steg-for-steg" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Steg-för-steg process</h2>

            <div className="space-y-6">
              {[
                {
                  step: 1,
                  title: "Stäm av bokföringen",
                  description:
                    "Säkerställ att all bokföring är korrekt och komplett. Kontrollera att alla transaktioner är registrerade och att konton är avstämda mot kontoutdrag.",
                },
                {
                  step: 2,
                  title: "Gör bokslutstransaktioner",
                  description:
                    "Bokför periodiseringar, avskrivningar, skatteskuld och eventuella avsättningar. Detta är grunden för en korrekt årsredovisning.",
                },
                {
                  step: 3,
                  title: "Exportera SIE-fil",
                  description:
                    "Exportera en SIE4-fil från ditt bokföringsprogram. Den innehåller alla saldon och transaktioner som behövs för årsredovisningen.",
                },
                {
                  step: 4,
                  title: "Skapa årsredovisningen",
                  description:
                    "Använd Kvanten för att ladda upp SIE-filen och generera årsredovisningen automatiskt. Komplettera med information som inte finns i bokföringen.",
                },
                {
                  step: 5,
                  title: "Håll årsstämma",
                  description:
                    "Kalla till årsstämma, presentera årsredovisningen och låt stämman fastställa resultat- och balansräkning samt besluta om resultatdisposition.",
                },
                {
                  step: 6,
                  title: "Signera årsredovisningen",
                  description:
                    "Samtliga styrelseledamöter och eventuell VD ska underteckna årsredovisningen. Detta kan göras digitalt.",
                },
                {
                  step: 7,
                  title: "Lämna in till Bolagsverket",
                  description:
                    "Skicka in årsredovisningen digitalt via Bolagsverkets e-tjänst eller som PDF. Spara kvittens på inlämningen.",
                },
              ].map((item) => (
                <div key={item.step} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-gray-600 mt-1">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 6 */}
          <section id="vanliga-misstag" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Vanliga misstag att undvika</h2>

            <div className="space-y-4">
              {[
                {
                  mistake: "Glömma periodiseringar",
                  solution:
                    "Kontrollera alltid om det finns intäkter eller kostnader som hör till räkenskapsåret men inte betalats än.",
                },
                {
                  mistake: "Felaktiga avskrivningar",
                  solution:
                    "Se till att avskrivningar beräknas korrekt enligt nyttjandeperiod. K2 tillåter schablon på 5 år.",
                },
                {
                  mistake: "Saknade noter",
                  solution:
                    "Vissa noter är obligatoriska beroende på vilka konton som har saldo. Kvanten identifierar automatiskt vilka noter som behövs.",
                },
                {
                  mistake: "Felaktig resultatdisposition",
                  solution:
                    "Utdelning kan inte överstiga fritt eget kapital. Kontrollera beloppet noggrant.",
                },
                {
                  mistake: "Missa underskrifter",
                  solution:
                    "Alla styrelseledamöter och VD måste skriva under. Glöm inte någon - det leder till avslag.",
                },
              ].map((item, i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-red-600 mb-2">❌ {item.mistake}</h3>
                  <p className="text-gray-600">
                    <strong className="text-green-600">✓ Lösning:</strong> {item.solution}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Section 7 */}
          <section id="tips" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Praktiska tips</h2>

            <div className="bg-blue-50 rounded-xl p-6">
              <ul className="space-y-4">
                {[
                  "Börja i god tid - vänta inte till sista veckan",
                  "Ha ordning på kvitton och underlag under hela året",
                  "Använd ett digitalt verktyg för att undvika manuella fel",
                  "Spara alltid en kopia av den signerade årsredovisningen",
                  "Kontrollera att balansräkningen stämmer (tillgångar = eget kapital + skulder)",
                  "Vid osäkerhet - konsultera en redovisningskonsult",
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              Redo att skapa din första årsredovisning?
            </h2>
            <p className="text-blue-100 mb-6">
              Med Kvanten går det snabbt och enkelt. Ladda upp din SIE-fil och ha årsredovisningen
              klar på under 30 minuter.
            </p>
            <Link
              to="/registrera"
              className="inline-flex items-center px-6 py-3 text-lg font-medium text-blue-600 bg-white rounded-lg hover:bg-blue-50 transition-colors"
            >
              Kom igång gratis
            </Link>
          </section>

          {/* Related articles */}
          <section className="mt-12">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Relaterade guider</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "Vad är en SIE-fil?",
                  href: "/arsredovisning/guider/sie-fil",
                  description: "Lär dig allt om SIE-formatet och hur du exporterar från bokföringen",
                },
                {
                  title: "K2 vs K3 - Vilket regelverk?",
                  href: "/arsredovisning/guider/k2-vs-k3",
                  description: "Förstå skillnaderna och välj rätt regelverk för ditt företag",
                },
              ].map((article, i) => (
                <Link
                  key={i}
                  to={article.href}
                  className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">{article.title}</h3>
                  <p className="text-sm text-gray-600">{article.description}</p>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </article>
    </>
  );
}
